import React, { useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'app/components/markdown/Markdown';
import { nextRevealLength, prepareStreamingMarkdown } from '../util/streamingMarkdown';
import { StreamingFooter, StreamingPlaceholder, StreamingText } from '../KaizenChatSession.styles';

// how often the revealed text grows; each step re-renders the markdown
const FRAME_MS = 32;

const PLACEHOLDER_LABELS = {
  table: 'Building table',
  diagram: 'Drawing diagram'
};

// How much of each streamed text has been revealed. When a response turns out to be reasoning rather than the
// answer it moves to another part of the step, and the view rendering it there picks up where this left off.
const revealedByStreamKey = new Map();

/**
 * Markdown that reveals itself at a steady pace while it streams in, and renders as plain `Markdown` once it is
 * complete -- the same element either way, so the finished response replaces the streamed one in place.
 *
 * Text that was never streamed (e.g. a loaded conversation) shows in full straight away.
 *
 * @param {string} streamKey identifies the response across views, e.g. its step id
 * @param {boolean} isStreaming whether more of `text` may still arrive
 * @param {React.ReactNode} footer shown below the text once all of it has been revealed
 */
const StreamingMarkdown = ({ text = '', isStreaming = false, streamKey, footer = null, ...markdownProps }) => {
  const [revealed, setRevealed] = useState(() => {
    if (streamKey && revealedByStreamKey.has(streamKey)) {
      return revealedByStreamKey.get(streamKey);
    }
    return isStreaming ? 0 : text.length;
  });
  const lastTickRef = useRef(null);
  const previousTextRef = useRef(text);

  // a retried request streams a different response; start that one over rather than keep the old one's progress
  let shown = Math.min(revealed, text.length);
  if (!text.startsWith(previousTextRef.current.slice(0, shown))) {
    shown = isStreaming ? 0 : text.length;
  }
  previousTextRef.current = text;

  useEffect(() => {
    if (streamKey && (isStreaming || revealedByStreamKey.has(streamKey))) {
      revealedByStreamKey.set(streamKey, shown);
    }
  }, [streamKey, isStreaming, shown]);

  useEffect(() => {
    if (shown >= text.length) {
      lastTickRef.current = null;
      return undefined;
    }

    let frame;
    const tick = (now) => {
      lastTickRef.current = lastTickRef.current ?? now;
      const elapsedMs = now - lastTickRef.current;
      if (elapsedMs >= FRAME_MS) {
        lastTickRef.current = now;
        const next = nextRevealLength({ text, revealed: shown, elapsedMs, isStreaming });
        if (next !== shown) {
          // the next render schedules the following step
          setRevealed(next);
          return;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [text, shown, isStreaming]);

  const isRevealing = isStreaming || shown < text.length;
  const { markdown, pending } = useMemo(
    () => (isRevealing ? prepareStreamingMarkdown(text.slice(0, shown)) : { markdown: text, pending: null }),
    [isRevealing, text, shown]
  );

  return (
    <>
      <StreamingText className={isRevealing ? 'kz-streaming' : undefined}>
        <Markdown {...markdownProps}>{markdown}</Markdown>
        {pending && <StreamingPlaceholder>{PLACEHOLDER_LABELS[pending]}</StreamingPlaceholder>}
      </StreamingText>
      {!isRevealing && footer && <StreamingFooter>{footer}</StreamingFooter>}
    </>
  );
};

export default StreamingMarkdown;
